import { MutableRefObject, useEffect, useRef } from "react";
import { featureCollection, point } from "@turf/turf";
import mapboxgl from "mapbox-gl";

import { generatingLayerId } from "../utils";
import { calculateMultiEntityZoomLevel } from "../workerMehods";
import styles from "./Popups.module.scss";

const DEFAULT_CENTER: [number, number] = [
  -156.44636531857395, 62.40265300551266,
];
const DEFAULT_ZOOM = 2.6339272096365725;
const FIT_BOUNDS_PADDING = 40;
const FIT_BOUNDS_DURATION = 2000;
const CUSTOM_LAYER_PREFIX = "point#";

const useCreateMarkers = (
  data: Entry[] | null,
  map: mapboxgl.Map | null,
  isMapFullyLoaded: boolean,
  mapType: string,
) => {
  const markersRef = useRef<mapboxgl.Marker[]>([]);

  useEffect(() => {
    if (!map || !isMapFullyLoaded) {
      return;
    }

    cleanupMapArtifacts(map, markersRef);

    if (data === null) {
      resetMapViewport(map);

      return () => cleanupMapArtifacts(map, markersRef);
    }

    const normalizedEntries = filterInvalidCoordinates(data);

    if (mapType === "markers") {
      markersRef.current = createMarkers(normalizedEntries, map);
    } else if (
      mapType === "cluster_with_numbers" ||
      mapType === "cluster_without_numbers"
    ) {
      createClusters(
        normalizedEntries,
        map,
        mapType === "cluster_with_numbers",
      );
    }

    if (normalizedEntries.length > 0) {
      fitMapToEntries(normalizedEntries, map);
    } else {
      resetMapViewport(map);
    }

    return () => cleanupMapArtifacts(map, markersRef);
  }, [data, map, isMapFullyLoaded, mapType]);

  return null;
};

export { useCreateMarkers };

function cleanupMapArtifacts(
  map: mapboxgl.Map,
  markersRef: MutableRefObject<mapboxgl.Marker[]>,
) {
  markersRef.current.forEach((marker) => marker.remove());
  markersRef.current = [];
  removeAllCustomLayers(map);
}

function coordToGeoJSON(coord: [number, number], count: number) {
  const pointFeature = point(coord, { count: formatNumber(count) });
  return featureCollection([pointFeature]);
}

function generateProperties(count: number) {
  if (count < 100) {
    return { radius: 20, textSize: 14, color: "#11A3E2" };
  }

  if (count < 1000) {
    return { radius: 40, textSize: 18, color: "#42BBAC" };
  }

  return { radius: 60, textSize: 20, color: "#BF200B" };
}

function formatNumber(num: number) {
  if (num < 1000) {
    return num.toString();
  }

  const units = ["k", "m", "b", "t"];
  let value = num;
  let unitIndex = -1;

  while (value >= 1000 && unitIndex < units.length - 1) {
    value /= 1000;
    unitIndex++;
  }

  const formattedValue =
    value % 1 !== 0 ? value.toFixed(1) : Math.floor(value).toString();

  return `${formattedValue}${units[unitIndex]}`;
}

function createMarkers(entries: Entry[], map: mapboxgl.Map) {
  const markers: mapboxgl.Marker[] = [];

  entries.forEach((entry) => {
    entry.locations.forEach((location) => {
      const coordinates = location.geometries[0]?.coordinates;
      const observationId = location.properties?.id;
      const observationCode = location.properties?.observationCode;

      if (!coordinates || !isValidLngLat(coordinates)) {
        return;
      }

      const popup = createObservationPopup(observationId, observationCode);

      markers.push(
        new mapboxgl.Marker({ draggable: false })
          .setLngLat(coordinates)
          .setPopup(popup)
          .addTo(map),
      );
    });
  });

  return markers;
}

function createClusters(
  entries: Entry[],
  map: mapboxgl.Map,
  isWithNumbers: boolean,
) {
  entries.forEach((entry, index) => {
    const coordinates = entry.locations[0]?.geometries[0]?.coordinates;

    if (!coordinates || !isValidLngLat(coordinates)) {
      return;
    }

    const { sourceId, layerId } = generatingLayerId("point", index);
    const count = entry.observationsCount ?? 0;
    const { radius, textSize, color } = generateProperties(count);

    if (map.getSource(sourceId) || map.getLayer(layerId)) {
      return;
    }

    map.addSource(sourceId, {
      type: "geojson",
      data: coordToGeoJSON(coordinates, count),
    });

    map.addLayer({
      id: layerId,
      type: "circle",
      source: sourceId,
      paint: {
        "circle-radius": radius,
        "circle-color": color,
      },
    });

    if (isWithNumbers) {
      map.addLayer({
        id: `${layerId}#text`,
        type: "symbol",
        source: sourceId,
        layout: {
          "text-field": ["get", "count"],
          "text-size": textSize,
        },
        paint: {
          "text-color": "#ffffff",
        },
      });
    }
  });
}

function removeAllCustomLayers(map: mapboxgl.Map) {
  const layers = map.getStyle()?.layers ?? [];
  const sourceIds = new Set<string>();

  [...layers]
    .reverse()
    .filter((layer) => layer.id.includes(CUSTOM_LAYER_PREFIX))
    .forEach((layer) => {
      if (map.getLayer(layer.id)) {
        map.removeLayer(layer.id);
      }

      const sourceId = getLayerSourceId(layer);

      if (sourceId) {
        sourceIds.add(sourceId);
      }
    });

  sourceIds.forEach((sourceId) => {
    if (map.getSource(sourceId)) {
      map.removeSource(sourceId);
    }
  });
}

function getLayerSourceId(layer: mapboxgl.AnyLayer) {
  return "source" in layer && typeof layer.source === "string"
    ? layer.source
    : null;
}

function fitMapToEntries(entries: Entry[], map: mapboxgl.Map) {
  const calculation = calculateMultiEntityZoomLevel(entries);

  if (!calculation?.bounds || !isLngLatBounds(calculation.bounds)) {
    return;
  }

  map.fitBounds(calculation.bounds, {
    padding: FIT_BOUNDS_PADDING,
    maxZoom: calculation.maxZoom,
    duration: FIT_BOUNDS_DURATION,
  });
}

function resetMapViewport(map: mapboxgl.Map) {
  map.flyTo({
    center: DEFAULT_CENTER,
    zoom: DEFAULT_ZOOM,
  });
}

type Geometry = {
  coordinates: [number, number];
  type: string;
};

type Location = {
  properties?: {
    id?: number;
    observationCode?: string | number;
    [key: string]: unknown;
  };
  geometries: Geometry[];
  type: string;
};

type Entry = {
  id: number;
  observationCode: string;
  locations: Location[];
  name: string;
  observationsCount?: number;
};

function isValidLngLat([lng, lat]: [number, number]) {
  return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

function isLngLatBounds(
  bounds: number[][],
): bounds is [[number, number], [number, number]] {
  return (
    bounds.length === 2 &&
    bounds.every(
      (point): point is [number, number] =>
        point.length === 2 &&
        typeof point[0] === "number" &&
        typeof point[1] === "number",
    )
  );
}

function filterInvalidCoordinates(entries: Entry[]) {
  return entries.flatMap((entry) => {
    const validLocations = entry.locations.flatMap((location) => {
      const hasValidGeometry = location.geometries.some((geometry) =>
        isValidLngLat(geometry.coordinates),
      );

      if (!hasValidGeometry) {
        return [];
      }

      return [
        {
          ...location,
          properties: {
            ...location.properties,
            id: entry.id,
            observationCode: entry.observationCode ?? entry.id,
          },
        },
      ];
    });

    if (validLocations.length === 0) {
      return [];
    }

    return [{ ...entry, locations: validLocations }];
  });
}

function createObservationPopup(
  observationId?: number,
  observationCode?: string | number,
) {
  const popup = document.createElement("div");
  popup.className = styles.observationPopup;

  const link = document.createElement("a");
  link.className = `${styles.obsCodeHeading} ${styles.observationPopupLink}`;
  link.textContent = String(observationCode ?? observationId ?? "");
  link.href = observationId ? `/observations/${observationId}/profile` : "#";
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  popup.appendChild(link);

  return new mapboxgl.Popup({
    closeOnClick: true,
    className: styles.mainPopups,
    closeButton: false,
    offset: [0, -18],
  }).setDOMContent(popup);
}
